import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../../../services/general/alert.service';
import { ToastService } from '../../../services/general/toast.service';
import { ProjectReportService } from '../../../services/project-report.service';
import {
  PROJECT_REPORT_FILE_TYPES,
  ProjectReport,
  ProjectReportFileType,
  projectReportFileIcon,
  projectReportFileType,
} from '../../../models/project-report.model';
import { ModalProjectReportComponent } from '../../../modals/modal-project-report/modal-project-report.component';

interface ReportFilters {
  search: string;
  uploader: string;
  type: 'ALL' | ProjectReportFileType;
  period: 'ALL' | '7' | '30' | '90';
}

const DEFAULT_FILTERS: ReportFilters = {
  search: '',
  uploader: 'ALL',
  type: 'ALL',
  period: 'ALL',
};

interface ColumnSetting {
  key: string;
  label: string;
  visible: boolean;
}

@Component({
  selector: 'app-project-reports',
  templateUrl: './project-reports.component.html',
  styleUrls: ['./project-reports.component.scss'],
  standalone: false,
})
export class ProjectReportsComponent implements OnChanges {
  @Input() projectId: number | string;

  readonly fileTypes = PROJECT_REPORT_FILE_TYPES;
  readonly projectReportFileIcon = projectReportFileIcon;
  readonly projectReportFileType = projectReportFileType;

  loading = false;
  canManage = false;
  filters: ReportFilters = { ...DEFAULT_FILTERS };
  uploaders: { id: string; name: string }[] = [];
  dataSource = new MatTableDataSource<ProjectReport>([]);

  columnSettings: ColumnSetting[] = [
    { key: 'notes', label: 'components.project-reports.column-notes', visible: true },
    { key: 'type', label: 'components.project-reports.column-type', visible: true },
    { key: 'file_size', label: 'components.project-reports.column-size', visible: true },
    { key: 'uploaded_by_name', label: 'components.project-reports.column-uploaded-by', visible: true },
    { key: 'created_at', label: 'components.project-reports.column-date', visible: true },
  ];

  constructor(
    private projectReportService: ProjectReportService,
    private dialog: MatDialog,
    private alertService: AlertService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    this.dataSource.filterPredicate = (report, serializedFilters) =>
      this.matchesFilters(report, JSON.parse(serializedFilters) as ReportFilters);

    this.dataSource.sortingDataAccessor = (report, column) => {
      switch (column) {
        case 'title':
          return report.title.toLowerCase();
        case 'type':
          return projectReportFileType(report.file_name);
        case 'uploaded_by_name':
          return (report.uploaded_by_name ?? '').toLowerCase();
        case 'created_at':
          return new Date(report.created_at).getTime();
        case 'file_size':
          return report.file_size;
        default:
          return (report[column] ?? '').toString().toLowerCase();
      }
    };
  }

  @ViewChild(MatPaginator) set paginator(paginator: MatPaginator) {
    this.dataSource.paginator = paginator;
  }

  @ViewChild(MatSort) set sort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  get displayedColumns(): string[] {
    const optional = this.columnSettings.filter((setting) => setting.visible).map((setting) => setting.key);
    return ['title', ...optional, 'actions'];
  }

  get hasActiveFilters(): boolean {
    return (
      this.filters.search.trim() !== '' ||
      this.filters.uploader !== DEFAULT_FILTERS.uploader ||
      this.filters.type !== DEFAULT_FILTERS.type ||
      this.filters.period !== DEFAULT_FILTERS.period
    );
  }

  get availableTypes(): ProjectReportFileType[] {
    const present = new Set(this.dataSource.data.map((report) => projectReportFileType(report.file_name)));
    return [...this.fileTypes.map((entry) => entry.type), 'other' as ProjectReportFileType].filter((type) =>
      present.has(type),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.load();
    }
  }

  load(): void {
    this.loading = true;
    this.projectReportService.list(
      this.projectId,
      (data) => {
        this.loading = false;
        this.canManage = data.can_manage;
        this.dataSource.data = data.reports;
        this.uploaders = this.buildUploaders(data.reports);
        this.applyFilters();
      },
      () => {
        this.loading = false;
      },
    );
  }

  applyFilters(): void {
    this.dataSource.filter = JSON.stringify(this.filters);
    this.dataSource.paginator?.firstPage();
  }

  resetFilters(): void {
    this.filters = { ...DEFAULT_FILTERS };
    this.applyFilters();
  }

  toggleColumn(setting: ColumnSetting): void {
    setting.visible = !setting.visible;
  }

  openAdd(): void {
    this.dialog
      .open(ModalProjectReportComponent, {
        data: { projectId: this.projectId },
        panelClass: 'medium-dialog',
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.toastService.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.project-reports.added'),
          );
          this.load();
        }
      });
  }

  openEdit(report: ProjectReport): void {
    this.dialog
      .open(ModalProjectReportComponent, {
        data: { projectId: this.projectId, report },
        panelClass: 'medium-dialog',
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.toastService.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.project-reports.updated'),
          );
          this.load();
        }
      });
  }

  view(report: ProjectReport): void {
    this.projectReportService.openFile(this.projectId, report.id);
  }

  remove(report: ProjectReport): void {
    this.alertService.showQuestion(
      this.translateService.instant('components.project-reports.delete-title'),
      this.translateService.instant('components.project-reports.delete-text', { title: report.title }),
      () =>
        this.projectReportService.remove(this.projectId, report.id, () => {
          this.toastService.success(
            this.translateService.instant('generic.success'),
            this.translateService.instant('components.project-reports.deleted'),
          );
          this.load();
        }),
    );
  }

  typeLabelKey(fileName: string): string {
    return 'components.project-reports.type-' + projectReportFileType(fileName);
  }

  formatSize(bytes: number): string {
    if (!bytes) {
      return '-';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(0) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  private matchesFilters(report: ProjectReport, filters: ReportFilters): boolean {
    const search = filters.search.trim().toLowerCase();
    if (search) {
      const haystack = [report.title, report.notes, report.file_name, report.uploaded_by_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) {
        return false;
      }
    }

    if (filters.uploader !== 'ALL' && String(report.uploaded_by_user_id) !== filters.uploader) {
      return false;
    }

    if (filters.type !== 'ALL' && projectReportFileType(report.file_name) !== filters.type) {
      return false;
    }

    if (filters.period !== 'ALL') {
      const threshold = Date.now() - Number(filters.period) * 24 * 60 * 60 * 1000;
      if (new Date(report.created_at).getTime() < threshold) {
        return false;
      }
    }

    return true;
  }

  private buildUploaders(reports: ProjectReport[]): { id: string; name: string }[] {
    const byId = new Map<string, string>();
    reports.forEach((report) => {
      if (report.uploaded_by_user_id !== null) {
        byId.set(String(report.uploaded_by_user_id), report.uploaded_by_name ?? String(report.uploaded_by_user_id));
      }
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }
}
